import React, {Fragment, useEffect, useRef, useState} from 'react';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {domAnimation, LazyMotion} from 'framer-motion';
import {queryClient} from '@common/http/query-client';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {SiteConfig} from '@app/site-config';
import deepMerge from 'deepmerge';
import {BaseSiteConfig} from '@common/core/settings/base-site-config';
import {ThemeProvider} from '@common/core/theme-provider';
import {AppearanceListener} from '@common/admin/appearance/commands/appearance-listener';
import {CookieNotice} from '@common/ui/cookie-notice/cookie-notice';
import {ToastContainer} from '@ui/toast/toast-container';
import {DialogStoreOutlet} from '@ui/overlays/store/dialog-store-outlet';
import {useSettings} from '@ui/settings/use-settings';
import {useAuth} from '@common/auth/use-auth';
import {verifyEmailRouter} from '@common/auth/ui/email-verification-page/email-verification-page';
import {
  createBrowserRouter,
  Outlet,
  RouterProvider,
  useNavigation,
  useRouteError,
} from 'react-router';
import {TopProgressBar} from '@ui/progress/top-progress-bar';
import {NotFoundPage} from '@common/ui/not-found-page/not-found-page';
import {PageErrorMessage} from '@common/errors/page-error-message';
import {userSuspendedRouter} from '@common/auth/ui/user-suspended-page/user-suspended-page';

const mergedConfig = deepMerge(BaseSiteConfig, SiteConfig);

type Router = ReturnType<typeof createBrowserRouter>;

interface Props {
  router: Router;
}
// SSR (Konu 1): Router'dan BAĞIMSIZ sağlayıcı yığını. Hem istemci CommonProvider
// hem de Node SSR entry (server-entry.tsx → StaticRouterProvider) aynı context
// ağacını paylaşsın diye ayrıldı → sürüklenme (drift) olmaz. Bu bileşen
// createBrowserRouter'a DOKUNMAZ, bu yüzden Node'da güvenle import edilir.
//
// `client`: yalnız SSR'da doldurulur (istek başına önbellek izolasyonu için).
// Verilmezse süreç ömürlü tekil istemci kullanılır → tarayıcı yolu değişmez.
export function CommonProviders({
  children,
  client = queryClient,
}: {
  children: React.ReactNode;
  client?: QueryClient;
}) {
  return (
    <QueryClientProvider client={client}>
      <LazyMotion features={domAnimation}>
        <SiteConfigContext.Provider value={mergedConfig}>
          <ThemeProvider>{children}</ThemeProvider>
        </SiteConfigContext.Provider>
      </LazyMotion>
    </QueryClientProvider>
  );
}

// todo: re-add strict
export function CommonProvider({router}: Props) {
  return (
    <CommonProviders>
      <CommonRouter router={router} />
    </CommonProviders>
  );
}

interface CommonRouterProps {
  router: Router;
}
function CommonRouter({router}: CommonRouterProps) {
  const {require_email_confirmation} = useSettings();
  const {user} = useAuth();

  if (user != null && require_email_confirmation && !user.email_verified_at) {
    return <RouterProvider router={verifyEmailRouter} />;
  }

  if (user != null && user.banned_at) {
    return <RouterProvider router={userSuspendedRouter} />;
  }

  return <RouterProvider router={router} />;
}

export function RootRoute() {
  return (
    <Fragment>
      <GlobalTopLoadingBar />
      <Outlet />
      <AppearanceListener />
      <CookieNotice />
      <ToastContainer />
      <DialogStoreOutlet />
    </Fragment>
  );
}

export function RootErrorElement() {
  const error = useRouteError();
  if ((error as any)?.status === 404) {
    return <NotFoundPage />;
  }
  return <PageErrorMessage />;
}

export function GlobalTopLoadingBar() {
  // SSR (Konu 1): TopProgressBar constructor'ı `document.createElement`
  // çağırır → Node'da crash. Yalnız tarayıcıda oluştur; sunucuda null (bu
  // bileşen zaten null render eder, `bar` sadece effect'te — client-only —
  // kullanılır, hydration markup'ı etkilenmez).
  const [bar] = useState(() =>
    typeof document !== 'undefined' ? new TopProgressBar() : null,
  );
  const {state} = useNavigation();
  const timeoutRef = useRef<any>(null);

  useEffect(() => {
    if (!bar) return;
    // react router will always set loading to true when "lazy" is set on route, even if that router is already loaded, this will result in loading bar showing for a few milliseconds
    if (state === 'loading') {
      timeoutRef.current = setTimeout(() => {
        bar.show();
      }, 50);
    } else {
      clearTimeout(timeoutRef.current);
      bar.hide();
    }

    return () => {
      if (state !== 'loading') {
        clearTimeout(timeoutRef.current);
        bar.hide();
      }
    };
  }, [state, bar]);

  return null;
}
